import React, { useState, useContext } from 'react';
import axios from 'axios';
import { LoginContext } from '../../Contexts/LoginContext';
import { generateExpenseStatement } from '../../utils/documentGenerator';
import { showToast } from './ShowToast';

const PERIOD_OPTIONS = [
  { value: 'week', label: 'Last 7 Days' },
  { value: 'month', label: 'Last 30 Days' },
  { value: '3month', label: 'Last 3 Months' },
  { value: '6month', label: 'Last 6 Months' },
  { value: 'year', label: 'Last 12 Months' },
  { value: 'custom', label: 'Custom Date Range' },
];

const getDateRange = (period, startDate, endDate) => {
  const now = new Date();
  let start, end;
  if (period === 'custom' && startDate && endDate) {
    start = new Date(startDate);
    end = new Date(endDate);
  } else {
    end = new Date(now);
    start = new Date(now);
    const map = { week: 7, month: 30, '3month': 90, '6month': 180, year: 365 };
    start.setDate(start.getDate() - (map[period] || 30));
  }
  return { start, end };
};

const filterByDateRange = (items, start, end, dateKey = 'createdAt') => {
  return items.filter((item) => {
    const d = new Date(item[dateKey] || item.date);
    return d >= start && d <= end;
  });
};

const DocumentModal = ({ isOpen, onClose, documentType, logoPath }) => {
  const { user, currency, exchangeRate } = useContext(LoginContext);
  const [period, setPeriod] = useState('month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const today = new Date().toISOString().split('T')[0];

  const handleGenerate = async () => {
    if (period === 'custom' && (!startDate || !endDate)) {
      showToast('Please select start and end dates', 'error');
      return;
    }
    if (period === 'custom' && new Date(startDate) > new Date(endDate)) {
      showToast('Start date must be before end date', 'error');
      return;
    }

    setIsGenerating(true);
    try {
      const { start, end } = getDateRange(period, startDate, endDate);
      const opts = { currency, exchangeRate, logoPath, period, startDate, endDate };
      const res = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/orders/buyer/current`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const orders = filterByDateRange(res.data.orders || [], start, end);
      await generateExpenseStatement(orders, user, opts);
      showToast('Expense Statement generated', 'success');
    } catch (err) {
      console.error(err);
      showToast('Failed to generate document', 'error');
    } finally {
      setIsGenerating(false);
    }
  };

  if (!isOpen) return null;

  const titles = {
    accountStatement: 'Account Statement',
    payoutStatement: 'Payout Statement',
    expenseStatement: 'Expense Statement',
  };

  return (
    <div className="modalOverlay" onClick={onClose}>
      <div className="modalContent documentModalContent" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '420px' }}>
        <h3>{titles[documentType] || 'Generate Document'}</h3>
        <p className="documentModalSubtitle">Select the time period for your statement</p>

        <div className="documentModalField">
          <label>Period</label>
          <select
            className="inputField"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          >
            {PERIOD_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {period === 'custom' && (
          <div className="documentModalDateRange">
            <div className="documentModalField">
              <label>Start Date</label>
              <input
                type="date"
                className="inputField"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={today}
              />
            </div>
            <div className="documentModalField">
              <label>End Date</label>
              <input
                type="date"
                className="inputField"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
                max={today}
              />
            </div>
          </div>
        )}

        <div className="documentModalActions">
          <button className="modalButton cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            className="modalButton submit"
            onClick={handleGenerate}
            disabled={isGenerating}
          >
            {isGenerating ? 'Generating...' : 'Generate PDF'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentModal;
